import React, { useState, useEffect, useMemo } from 'react';

const SmartVocabularyViewer = ({ courseId, week = 1 }) => {
  const [vocabulary, setVocabulary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredTerm, setHoveredTerm] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const loadVocabulary = async () => {
      try {
        const response = await fetch(`/coursework/${courseId}/week${week}/vocabulary.json`);
        if (!response.ok) {
          throw new Error('Vocabulary not available for this week');
        }
        const data = await response.json();
        setVocabulary(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadVocabulary();
  }, [courseId, week]);

  // Create a term lookup map for cross-referencing
  const termLookup = useMemo(() => {
    if (!vocabulary?.terms) return {};
    
    const lookup = {};
    vocabulary.terms.forEach(term => {
      // Add the main term
      lookup[term.term.toLowerCase()] = term;
      
      // Add common variations and plurals
      const variations = generateTermVariations(term.term);
      variations.forEach(variation => {
        lookup[variation.toLowerCase()] = term;
      });
    });
    
    return lookup;
  }, [vocabulary]);

  // Generate common variations of a term
  const generateTermVariations = (term) => {
    const variations = [term];
    
    // Add plural forms
    if (!term.endsWith('s')) {
      variations.push(term + 's');
    }
    if (term.endsWith('y')) {
      variations.push(term.slice(0, -1) + 'ies');
    }
    
    // Add singular forms
    if (term.endsWith('s') && term.length > 1) {
      variations.push(term.slice(0, -1));
    }
    if (term.endsWith('ies')) {
      variations.push(term.slice(0, -3) + 'y');
    }
    
    // Add common compound word variations
    const words = term.split(' ');
    if (words.length > 1) {
      variations.push(words.join(''));
      variations.push(words.join('-'));
    }
    
    return variations;
  };

  // Enhanced text processing to identify and link cross-referenced terms
  const processTextWithCrossReferences = (text) => {
    if (!text || !termLookup) return text;

    // Create a list of all terms to look for, sorted by length (longest first)
    const termsToFind = Object.keys(termLookup)
      .filter(term => term.length > 2) // Avoid very short terms that might cause false matches
      .sort((a, b) => b.length - a.length);

    let processedText = text;
    const replacements = [];

    // Find all term matches
    termsToFind.forEach(termKey => {
      const term = termLookup[termKey];
      const regex = new RegExp(`\\b${escapeRegExp(termKey)}\\b`, 'gi');
      let match;

      while ((match = regex.exec(text)) !== null) {
        const startIndex = match.index;
        const endIndex = match.index + match[0].length;
        
        // Avoid overlapping replacements
        const hasOverlap = replacements.some(replacement => 
          (startIndex < replacement.end && endIndex > replacement.start)
        );

        if (!hasOverlap) {
          replacements.push({
            start: startIndex,
            end: endIndex,
            original: match[0],
            term: term,
            replacement: `<span class="vocabulary-term" data-term="${JSON.stringify(term).replace(/"/g, '&quot;')}">${match[0]}</span>`
          });
        }
      }
    });

    // Sort replacements by start position (descending) to avoid index shifting
    replacements.sort((a, b) => b.start - a.start);

    // Apply replacements
    replacements.forEach(replacement => {
      processedText = 
        processedText.slice(0, replacement.start) + 
        replacement.replacement + 
        processedText.slice(replacement.end);
    });

    return processedText;
  };

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Handle hover events for cross-referenced terms
  const handleTermHover = (event, termData) => {
    if (termData) {
      setHoveredTerm(termData);
      setHoverPosition({ 
        x: event.clientX, 
        y: event.clientY 
      });
    }
  };

  const handleTermLeave = () => {
    setHoveredTerm(null);
  };

  // Enhanced text renderer component
  const EnhancedTextRenderer = ({ text, className = '' }) => {
    const processedHtml = processTextWithCrossReferences(text);
    
    useEffect(() => {
      // Add event listeners to dynamically created term elements
      const termElements = document.querySelectorAll('.vocabulary-term');
      
      const handleMouseEnter = (e) => {
        try {
          const termData = JSON.parse(e.target.getAttribute('data-term'));
          handleTermHover(e, termData);
        } catch (error) {
          console.warn('Error parsing term data:', error);
        }
      };

      const handleMouseLeave = () => {
        handleTermLeave();
      };

      termElements.forEach(element => {
        element.addEventListener('mouseenter', handleMouseEnter);
        element.addEventListener('mouseleave', handleMouseLeave);
      });

      // Cleanup
      return () => {
        termElements.forEach(element => {
          element.removeEventListener('mouseenter', handleMouseEnter);
          element.removeEventListener('mouseleave', handleMouseLeave);
        });
      };
    }, [processedHtml]);

    return (
      <div 
        className={className}
        dangerouslySetInnerHTML={{ __html: processedHtml }}
      />
    );
  };

  const downloadVocabulary = (format) => {
    if (!vocabulary) return;
    
    let content, filename, mimeType;
    
    switch (format) {
      case 'json':
        content = JSON.stringify(vocabulary, null, 2);
        filename = `${courseId}_week${week}_vocabulary.json`;
        mimeType = 'application/json';
        break;
      case 'csv':
        const headers = 'Term,Definition,Category,Example\n';
        const csvContent = vocabulary.terms.map(term => 
          `"${term.term}","${term.definition}","${term.category}","${term.example || ''}"`
        ).join('\n');
        content = headers + csvContent;
        filename = `${courseId}_week${week}_vocabulary.csv`;
        mimeType = 'text/csv';
        break;
      case 'txt':
        content = vocabulary.terms.map(term => 
          `${term.term}: ${term.definition}${term.example ? `\nExample: ${term.example}` : ''}\n`
        ).join('\n');
        filename = `${courseId}_week${week}_vocabulary.txt`;
        mimeType = 'text/plain';
        break;
      default:
        return;
    }
    
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-lg mb-2">📝</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Vocabulary Available</h3>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const categories = [...new Set(vocabulary.terms.map(term => term.category))];
  const filteredTerms = vocabulary.terms.filter(term => {
    const matchesSearch = term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         term.definition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || term.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Add CSS for vocabulary term styling */}
      <style dangerouslySetInnerHTML={{__html: `
        .vocabulary-term {
          color: #3b82f6;
          cursor: pointer;
          border-bottom: 1px dotted #3b82f6;
          transition: all 0.2s ease;
        }
        .vocabulary-term:hover {
          color: #1d4ed8;
          background-color: #eff6ff;
          border-bottom-style: solid;
        }
      `}}/>

      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">📚 Week {week} Smart Vocabulary</h2>
            <p className="text-gray-600">{vocabulary.title}</p>
            <p className="text-sm text-blue-600 mt-1">
              ✨ Hover over highlighted terms in definitions to see cross-references
            </p>
          </div>
          <div className="flex items-center gap-2">
            {vocabulary.download_formats?.map(format => (
              <button
                key={format}
                onClick={() => downloadVocabulary(format)}
                className="px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
              >
                ↓ {format.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Study Guide */}
        {vocabulary.study_guide && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="text-blue-600 text-lg">💡</div>
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Study Guide</h4>
                <EnhancedTextRenderer 
                  text={vocabulary.study_guide}
                  className="text-sm text-blue-800"
                />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search terms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Terms List */}
      <div className="p-6">
        <div className="text-sm text-gray-500 mb-4">
          Showing {filteredTerms.length} of {vocabulary.terms.length} terms
          {Object.keys(termLookup).length > 0 && (
            <span className="ml-2 text-blue-600">
              • {Object.keys(termLookup).length} cross-reference links active
            </span>
          )}
        </div>

        <div className="space-y-4">
          {filteredTerms.map((term, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{term.term}</h3>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  {term.category.replace('_', ' ')}
                </span>
              </div>
              <EnhancedTextRenderer 
                text={term.definition}
                className="text-gray-700 mb-2 leading-relaxed"
              />
              {term.example && (
                <div className="bg-gray-50 border-l-4 border-blue-400 p-3 rounded-r-md">
                  <p className="text-sm text-gray-600">
                    <strong>Example:</strong> <EnhancedTextRenderer text={term.example} />
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredTerms.length === 0 && (
          <div className="text-center py-8">
            <div className="text-gray-400 text-lg mb-2">🔍</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Terms Found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
          </div>
        )}
      </div>

      {/* Floating Definition Tooltip */}
      {hoveredTerm && (
        <div 
          className="fixed z-50 max-w-sm bg-gray-900 text-white text-sm rounded-lg p-3 shadow-xl pointer-events-none"
          style={{ 
            left: `${Math.min(hoverPosition.x + 10, window.innerWidth - 250)}px`, 
            top: `${Math.max(hoverPosition.y - 10, 10)}px` 
          }}
        >
          <div className="font-semibold text-blue-300 mb-1">
            {hoveredTerm.term}
          </div>
          <div className="text-gray-200 text-xs leading-relaxed">
            {hoveredTerm.definition}
          </div>
          {hoveredTerm.category && (
            <div className="text-gray-400 text-xs mt-1 italic">
              Category: {hoveredTerm.category.replace('_', ' ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SmartVocabularyViewer;